/**
 * CrossBranchStockView — the shared body of the "any branch can see stock
 * at every other branch" table. Mounted at three surfaces:
 *   1. /ha/all-branch-stock (standalone page)
 *   2. Product Catalogue → "Availability" tab
 *   3. Transfers page → "Availability" tab
 *
 * Read-only. Fetches /api/ha/inventory/cross-branch-stock and paints a
 * matrix: brand / model / type / colours-in-stock / powers-in-stock /
 * sizes-in-stock / per-branch qty columns / total.
 *
 * Also renders a "Request from another branch" CTA per row that jumps to
 * /ha/requests?product_id=... (user's stated primary use-case: "raise a
 * request for their desired product").
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Search, RefreshCw, AlertTriangle, Send, Factory,
} from 'lucide-react';
import { COLOR_SWATCHES } from '../../lib/haSpecs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Match Product Catalogue's form_factor labels 1:1.
const FORM_FACTOR_LABELS = {
  RIC: 'RIC', BTE: 'BTE', ITE: 'ITE', ITC: 'ITC', CIC: 'CIC', IIC: 'IIC',
  POCKET: 'Pocket Aids', accessory: 'Accessory',
};
const HA_FORM_FACTORS = ['', 'RIC', 'BTE', 'ITE', 'ITC', 'CIC', 'IIC', 'POCKET'];

export default function CrossBranchStockView({ embedded = false, testidPrefix = 'cbs' }) {
  const [data, setData] = useState({ branches: [], rows: [], branch_totals: {}, grand_total: 0 });
  const [search, setSearch] = useState('');
  const [formFactor, setFormFactor] = useState('');
  const [includeAccessories, setIncludeAccessories] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = { include_accessories: includeAccessories };
      if (search) params.search = search;
      if (formFactor) params.form_factor = formFactor;
      const r = await axios.get(`${API}/ha/inventory/cross-branch-stock`, { params });
      setData(r.data || { branches: [], rows: [] });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load cross-branch stock');
    } finally {
      setLoading(false);
    }
  }, [search, formFactor, includeAccessories]);

  useEffect(() => { load(); }, [load]);

  // Debounce search — same UX pattern used elsewhere on Inventory pages.
  const [pendingSearch, setPendingSearch] = useState(search);
  useEffect(() => {
    const h = setTimeout(() => setSearch(pendingSearch), 250);
    return () => clearTimeout(h);
  }, [pendingSearch]);

  const branches = data.branches || [];
  const rows = data.rows || [];

  return (
    <div className={embedded ? '' : 'p-4 sm:p-6'} data-testid={`${testidPrefix}-root`}>
      {!embedded && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <Factory size={18} className="text-indigo-600" />
            <h1 className="text-lg font-bold text-slate-800">Cross-Branch Availability</h1>
          </div>
          <p className="text-[11px] text-slate-500 max-w-3xl">
            Live IN_STOCK counts for every HA SKU across every branch in your clinic.
            Search a make / model, pick a form-factor filter, and use{' '}
            <b>Request Transfer</b> to raise a stock request when your branch is short.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-3 flex flex-wrap items-center gap-2"
           data-testid={`${testidPrefix}-filters`}>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            placeholder="Search brand / model…"
            data-testid={`${testidPrefix}-search`}
            className="w-full text-sm bg-white border border-slate-300 rounded-md pl-8 pr-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
        <select
          value={formFactor}
          onChange={(e) => setFormFactor(e.target.value)}
          data-testid={`${testidPrefix}-form-filter`}
          className="text-sm bg-white border border-slate-300 rounded-md px-2 py-1.5"
        >
          {HA_FORM_FACTORS.map((f) => (
            <option key={f || 'all'} value={f}>
              {f ? (FORM_FACTOR_LABELS[f] || f) : 'All HA types'}
            </option>
          ))}
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 select-none"
               data-testid={`${testidPrefix}-inc-acc-label`}>
          <input
            type="checkbox"
            checked={includeAccessories}
            onChange={(e) => setIncludeAccessories(e.target.checked)}
            data-testid={`${testidPrefix}-inc-acc`}
          />
          Include accessories
        </label>
        <button
          onClick={load}
          disabled={loading}
          data-testid={`${testidPrefix}-refresh`}
          className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 px-3 py-1.5 rounded"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
        <div className="ml-auto text-[11px] text-slate-500 tabular-nums">
          {rows.length} SKU{rows.length === 1 ? '' : 's'} · {data.grand_total || 0} unit{(data.grand_total || 0) === 1 ? '' : 's'} total
        </div>
      </div>

      {err && (
        <div className="mb-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2 rounded flex items-start gap-2"
             data-testid={`${testidPrefix}-error`}>
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
          <div>{String(err)}</div>
        </div>
      )}

      {/* Matrix */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${testidPrefix}-table`}>
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left sticky left-0 bg-slate-50 z-10">Brand · Model</th>
              <th className="px-2 py-2 text-left">Type</th>
              <th className="px-2 py-2 text-left">Colours in stock</th>
              <th className="px-2 py-2 text-left">Powers</th>
              <th className="px-2 py-2 text-left">Sizes</th>
              {branches.map((b) => (
                <th
                  key={b.branch_id}
                  className="px-2 py-2 text-right whitespace-nowrap"
                  data-testid={`${testidPrefix}-hdr-${b.branch_id}`}
                >
                  {b.name}
                  {b.is_primary && (
                    <span className="ml-1 text-[9px] font-bold text-indigo-500">HQ</span>
                  )}
                </th>
              ))}
              <th className="px-2 py-2 text-right bg-slate-100">Total</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6 + branches.length} className="text-center py-10 text-slate-400 italic text-sm">
                  Loading availability…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6 + branches.length} className="text-center py-10 text-slate-400 italic text-sm"
                    data-testid={`${testidPrefix}-empty`}>
                  No stock matches this filter across any branch. Try clearing filters or
                  toggling <b>Include accessories</b>.
                </td>
              </tr>
            ) : rows.map((r) => (
              <StockRow
                key={r.product_id}
                r={r}
                branches={branches}
                testidPrefix={testidPrefix}
              />
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50 text-xs font-bold text-slate-700"
                  data-testid={`${testidPrefix}-footer`}>
                <td className="px-3 py-2 sticky left-0 bg-slate-50" colSpan={5}>
                  Branch totals
                </td>
                {branches.map((b) => (
                  <td
                    key={b.branch_id}
                    className="px-2 py-2 text-right tabular-nums"
                    data-testid={`${testidPrefix}-total-${b.branch_id}`}
                  >
                    {data.branch_totals?.[b.branch_id] || 0}
                  </td>
                ))}
                <td className="px-2 py-2 text-right tabular-nums bg-slate-100">
                  {data.grand_total || 0}
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function StockRow({ r, branches, testidPrefix }) {
  const chip = (val) => (
    <span className="inline-block text-[9.5px] font-semibold bg-slate-100 text-slate-700 rounded px-1.5 py-0.5 mr-0.5 mb-0.5">
      {val}
    </span>
  );
  // Colour chip: swatch dot in the actual shell tint next to the value.
  // `other` uses a dashed empty circle so an unknown factory colour
  // isn't mis-represented by an arbitrary fake tint.
  const colorChip = (val) => {
    const swatch = COLOR_SWATCHES[val];
    const dotStyle = swatch && swatch !== 'transparent'
      ? { background: swatch, border: '1px solid rgba(0,0,0,0.18)' }
      : { background: 'transparent', border: '1px dashed rgba(0,0,0,0.3)' };
    return (
      <span
        className="inline-flex items-center gap-[3px] text-[9.5px] font-semibold bg-slate-100 text-slate-700 rounded px-1.5 py-0.5 mr-0.5 mb-0.5"
        data-testid={`${testidPrefix}-color-chip-${val}`}
      >
        <span
          aria-hidden
          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
          style={dotStyle}
        />
        {val}
      </span>
    );
  };
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/40"
        data-testid={`${testidPrefix}-row-${r.product_id}`}>
      <td className="px-3 py-2 sticky left-0 bg-white group-hover:bg-slate-50/40 z-10">
        <div className="font-semibold text-slate-800">{r.brand}</div>
        <div className="text-[11px] text-slate-500">{r.model}</div>
      </td>
      <td className="px-2 py-2 text-[11px]">
        <span className="inline-block bg-indigo-50 text-indigo-800 rounded px-1.5 py-0.5 uppercase text-[9.5px] font-bold">
          {FORM_FACTOR_LABELS[r.form_factor] || r.form_factor}
        </span>
        {r.tech_tier && (
          <span className="ml-1 text-[10px] text-slate-500 capitalize">{r.tech_tier}</span>
        )}
        {r.ear_side && r.form_factor === 'accessory' && (
          <div className="text-[10px] font-semibold text-emerald-700 mt-0.5 capitalize">
            {r.ear_side}
          </div>
        )}
      </td>
      <td className="px-2 py-2 max-w-[180px]">
        {(r.colors_in_stock || []).length === 0 ? (
          <span className="italic text-slate-300 text-[10.5px]">—</span>
        ) : (
          <div className="flex flex-wrap">{r.colors_in_stock.map((c) => <React.Fragment key={c}>{colorChip(c)}</React.Fragment>)}</div>
        )}
      </td>
      <td className="px-2 py-2 max-w-[120px]">
        {(r.powers_in_stock || []).length === 0 ? (
          <span className="italic text-slate-300 text-[10.5px]">—</span>
        ) : (
          <div className="flex flex-wrap">{r.powers_in_stock.map((p) => <React.Fragment key={p}>{chip(p)}</React.Fragment>)}</div>
        )}
      </td>
      <td className="px-2 py-2 max-w-[120px]">
        {(r.sizes_in_stock || []).length === 0 ? (
          <span className="italic text-slate-300 text-[10.5px]">—</span>
        ) : (
          <div className="flex flex-wrap">{r.sizes_in_stock.map((sz) => <React.Fragment key={sz}>{chip(sz)}</React.Fragment>)}</div>
        )}
      </td>
      {branches.map((b) => {
        const qty = r.cells?.[b.branch_id] || 0;
        return (
          <td key={b.branch_id}
              className={`px-2 py-2 text-right tabular-nums ${
                qty === 0 ? 'text-slate-300' : qty <= 2 ? 'text-amber-700 font-semibold' : 'text-slate-800 font-semibold'
              }`}
              data-testid={`${testidPrefix}-cell-${r.product_id}-${b.branch_id}`}>
            {qty}
          </td>
        );
      })}
      <td className="px-2 py-2 text-right tabular-nums bg-slate-50 font-bold text-slate-800"
          data-testid={`${testidPrefix}-total-row-${r.product_id}`}>
        {r.total}
      </td>
      <td className="px-2 py-2 text-right">
        <Link
          to={`/ha/requests?product_id=${r.product_id}`}
          data-testid={`${testidPrefix}-request-${r.product_id}`}
          title="Request transfer of this SKU from another branch"
          className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-indigo-700 hover:text-indigo-900 hover:underline whitespace-nowrap"
        >
          <Send size={10} /> Request
        </Link>
      </td>
    </tr>
  );
}
